import { gql, useApolloClient, useMutation, useQuery } from '@apollo/client';
import IconOutlink from '@apollo/icons/default/IconOutlink.svg';
import {
  Alert,
  Button,
  ButtonGroup,
  Modal,
  ModalBody,
  ModalContent,
  ModalFooter,
  ModalHeader,
  ModalOverlay,
  ModalTitle,
} from '@apollo/orbit';
import React, { useCallback, useState } from 'react';

import { Form as FormWrapper } from 'src/components/common/form/Form';
import { Loading } from 'src/components/common/loading/Loading';
import { ErrorMessage } from 'src/components/errorMessage/ErrorMessage';
import {
  PQListDeleteModalMutation,
  PQListDeleteModalMutationVariables,
  PQListDeleteModalQuery,
  PQListDeleteModalQueryVariables,
} from 'src/lib/graphqlTypes/types';

interface Props {
  isOpen: boolean;
  graphId: string;
  listId: string;
  onClose: () => void;
  onRedirectToUnlink: () => void;
  __test?: {
    mutationError?: Error;
    loading?: Boolean;
    submitting?: Boolean;
  };
}

const pqListDeleteModalQuery = gql<
  PQListDeleteModalQuery,
  PQListDeleteModalQueryVariables
>`
  query PQListDeleteModalQuery($graphId: ID!, $listId: ID!) {
    graph(id: $graphId) {
      id
      persistedQueryList(id: $listId) {
        id
        name
        description
        currentBuild {
          id
          totalOperationsInList
        }
        linkedVariants {
          id
          name
        }
      }
    }
  }
`;

const pqListDeleteModalMutation = gql<
  PQListDeleteModalMutation,
  PQListDeleteModalMutationVariables
>`
  mutation PQListDeleteModalMutation($graphId: ID!, $listId: ID!) {
    service(id: $graphId) {
      persistedQueryList(id: $listId) {
        id
        delete {
          ... on DeletePersistedQueryListResult {
            __typename
          }
          ... on CannotDeleteLinkedPersistedQueryListError {
            message
            __typename
          }
          ... on PermissionError {
            message
            __typename
          }
        }
      }
    }
  }
`;

export const PersistedQueryListDeleteModal = ({
  isOpen,
  graphId,
  listId,
  onClose,
  onRedirectToUnlink,
  __test,
}: Props) => {
  const {
    data: pqListData,
    loading: pqListLoading,
    error: pqListError,
  } = useQuery(pqListDeleteModalQuery, {
    variables: { graphId, listId },
  });

  const pqList = pqListData?.graph?.persistedQueryList;
  const client = useApolloClient();

  const [mutate, { loading: mutationLoading, error: mutationError }] =
    useMutation(pqListDeleteModalMutation, {
      variables: { graphId, listId },
      onCompleted: () => {
        if (!pqList) return;
        client.cache.evict({
          id: client.cache.identify({
            __typename: pqList.__typename,
            id: pqList.id,
          }),
        });
      },
    });
  const [failureMessage, setFailureMessage] = useState<string | undefined>();

  const handelSubmit = useCallback(async () => {
    setFailureMessage(undefined);
    const { data } = await mutate();
    if (!data?.service?.persistedQueryList) {
      // we handle errors reactively above
      return;
    }
    switch (data.service.persistedQueryList.delete.__typename) {
      case 'DeletePersistedQueryListResult':
        onClose();
        break;
      default:
        setFailureMessage(data.service.persistedQueryList.delete.message);
        break;
    }
  }, [mutate, onClose]);

  const loading = pqListLoading || Boolean(__test?.loading);
  const submitting = mutationLoading || Boolean(__test?.submitting);
  const hasLinkedVariants = (pqList?.linkedVariants.length ?? 0) > 0;

  if (loading) {
    return (
      <Modal isOpen={isOpen} size="xl" onClose={onClose}>
        <ModalOverlay />
        <ModalContent>
          <ModalBody>
            <FormWrapper className="py-4" onSubmit={onClose}>
              <Loading size="md" />
            </FormWrapper>
          </ModalBody>
        </ModalContent>
      </Modal>
    );
  }

  if (pqList && hasLinkedVariants) {
    return (
      <Modal isOpen={isOpen} size="xl" onClose={onClose}>
        <ModalOverlay />
        <FormWrapper onSubmit={onClose}>
          <ModalContent>
            <ModalHeader>
              <ModalTitle>Persisted query list cannot be deleted</ModalTitle>
            </ModalHeader>
            <ModalBody>
              <Alert status="warning">
                The <em>{pqList.name}</em> list is linked to the following
                variants:{' '}
                <ul className="my-2 ml-6 list-disc">
                  {pqList.linkedVariants.map((v) => (
                    <li key={v.id}>
                      <em>{v.name}</em>
                    </li>
                  ))}
                </ul>
                Once all variants are unlinked, you'll be able to delete it.
              </Alert>
            </ModalBody>
            <ModalFooter>
              <ButtonGroup>
                <Button variant="secondary" type="submit">
                  Close
                </Button>
                <Button
                  variant="primary"
                  rightIcon={<IconOutlink />}
                  type="button"
                  onClick={onRedirectToUnlink}
                >
                  Unlink Variants Now
                </Button>
              </ButtonGroup>
            </ModalFooter>
          </ModalContent>
        </FormWrapper>
      </Modal>
    );
  }

  return (
    <Modal isOpen={isOpen} size="xl" onClose={onClose}>
      <ModalOverlay />
      <FormWrapper onSubmit={handelSubmit}>
        <ModalContent>
          <ModalHeader>
            <ModalTitle>Are you sure you want to delete this list?</ModalTitle>
          </ModalHeader>
          <ModalBody>
            {!pqList ? null : pqList.currentBuild.totalOperationsInList > 0 ? (
              <p>
                The <em>{pqList.name}</em> list has{' '}
                {pqList.currentBuild.totalOperationsInList} operations published
                to it. This action cannot be undone.
              </p>
            ) : (
              <p>
                Deleting the <em>{pqList.name}</em> list cannot be undone.
              </p>
            )}
            {(pqListError || mutationError || __test?.mutationError) && (
              <ErrorMessage>
                Something went wrong. Please refresh and try again.
              </ErrorMessage>
            )}
            {failureMessage && <ErrorMessage>{failureMessage}</ErrorMessage>}
          </ModalBody>
          <ModalFooter>
            <ButtonGroup>
              <Button
                variant="secondary"
                type="button"
                isDisabled={submitting}
                onClick={onClose}
              >
                Cancel
              </Button>
              <Button
                variant="destructive"
                isDisabled={loading || submitting || hasLinkedVariants}
                isLoading={submitting}
                loadingText="Yes, delete"
                type="submit"
              >
                Yes, delete
              </Button>
            </ButtonGroup>
          </ModalFooter>
        </ModalContent>
      </FormWrapper>
    </Modal>
  );
};
